<template>
  <main class="company-info">
    <!-- Header -->
    <div class="company-info__header">
      <div class="company-info__header-content">
        <h1 class="company-info__title">Company Information</h1>
        <p class="company-info__subtitle">Manage your organization's profile and details</p>
      </div>
      <button
        class="company-info__edit-btn"
        @click="editInfo = true"
        v-if="!organisationDetails.officeAddress || !organisationDetails.country || !organisationDetails.state"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.334 2.00004C11.5091 1.82494 11.7169 1.68605 11.9457 1.59129C12.1745 1.49653 12.4197 1.44775 12.6673 1.44775C12.915 1.44775 13.1680 1.49653 13.3889 1.59129C13.6177 1.68605 13.8255 1.82494 14.0007 2.00004C14.1758 2.17513 14.3147 2.383 14.4094 2.61178C14.5042 2.84055 14.5765 3.08575 14.553 3.33337C14.553 3.581 14.5042 3.8262 14.4094 4.05497C14.3147 4.28374 14.1758 4.49161 14.0007 4.66671L5.00065 13.6667L1.33398 14.6667L2.33398 11L11.334 2.00004Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Edit Info
      </button>
    </div>

    <!-- Company Logo Section -->
    <div class="company-info__section">
      <div class="company-info__logo-area">
        <div class="company-info__logo-wrapper">
          <div class="company-info__logo-loading" v-if="loading">
            <div class="company-info__spinner"></div>
          </div>
          <img
            class="company-info__logo"
            :src="organisationDetails.image || 'https://res.cloudinary.com/djalafcj9/image/upload/v1643388690/getequityV2/investor/Vector_ymdocc.webp'"
            :alt="organisationDetails.name"
          />
        </div>
        <div class="company-info__logo-actions">
          <label for="file" class="company-info__upload-btn">
            <input
              type="file"
              name="image"
              class="company-info__file-input"
              title="upload"
              ref="tokenImage"
              id="file"
              accept="image/png, image/jpeg, image/jpg"
              @change="previewImage"
            />
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.75 6L9 2.25L5.25 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 2.25V11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Upload Logo
          </label>
          <span class="company-info__upload-hint">PNG, JPG up to 5MB</span>
        </div>
      </div>
      <p class="company-info__error" v-if="fileMessage">{{ fileMessage }}</p>
    </div>

    <!-- Info Grid -->
    <div class="company-info__grid">
      <div class="company-info__item">
        <span class="company-info__label">Company Name</span>
        <span class="company-info__value">{{ organisationDetails.name || '-' }}</span>
      </div>
      <div class="company-info__item">
        <span class="company-info__label">Industry</span>
        <span class="company-info__value">{{ organisationDetails.industry || '-' }}</span>
      </div>
      <div class="company-info__item">
        <span class="company-info__label">Email Address</span>
        <span class="company-info__value">{{ organisationDetails.email || '-' }}</span>
      </div>
      <div class="company-info__item">
        <span class="company-info__label">Phone Number</span>
        <span class="company-info__value">{{ organisationDetails.phone || '-' }}</span>
      </div>
      <div class="company-info__item">
        <span class="company-info__label">Country of Incorporation</span>
        <span class="company-info__value">{{ orgCountry || '-' }}</span>
      </div>
      <div class="company-info__item">
        <span class="company-info__label">Office Address</span>
        <span class="company-info__value">{{ organisationDetails.officeAddress || '-' }}</span>
      </div>
      <div class="company-info__item">
        <span class="company-info__label">State / Region</span>
        <span class="company-info__value">{{ organisationDetails.state || '-' }}</span>
      </div>
    </div>

    <!-- Edit Modal -->
    <PersonalInfo
      v-if="editInfo"
      @close="editInfo = false"
      @info-updated="getOrganisationProfile"
    />
  </main>
</template>

<script>
  import PersonalInfo from "@/components/account/PersonalInfo";
  import { mapActions, mapGetters } from "vuex";
  import countryRegionState from "@/utils/js/countryRegionData";
  import axios from "axios";
  import responseHandler from "@/utils/responseHandler.js";
  export default {
    name: "CompanyInfo",
    data() {
      return {
        countryRegionState,
        editInfo: false,
        showDragNdrop: false,
        fileMessage: "",
        image: "",
        loading: false,
      };
    },
    components: {
      PersonalInfo,
    },
    computed: {
      ...mapGetters("organisationModule", ["organisationDetails"]),
      orgCountry() {
        let country = null;
        let countryInfo = this.countryRegionState.filter(
          (country) =>
            country.countryShortCode === this.organisationDetails.country ||
            country.countryName === this.organisationDetails.country
        );
        country = countryInfo.length > 0 && countryInfo[0].countryName;
        return country;
      },
    },
    watch: {
      image(val) {
        let currentValue = val;
        setTimeout(() => {
          if (
            val &&
            currentValue === val &&
            !val.includes("https://res.cloudinary.com/")
          ) {
            this.uploadImage();
          }
        }, 1000);
      },
    },
    methods: {
      ...mapActions("organisationModule", [
        "getOrganisationProfile",
        "updateOrgDetails",
      ]),
      ...mapActions(["showToast"]),
      ...mapActions("fileUploads", ["setStatus", "setLoading"]),
      previewImage(event, type) {
        // Reference to the DOM input element
        const input = event.target || event;
        const check = type === "dragNdrop" ? event : input.files;
        if (check.length > 0) {
          const size = check[0].size;
          const file = Math.round(size / 1024);

          // Ensure that you have a file before attempting to read it
          if (check && check[0]) {
            if (file > 5120) {
              this.fileMessage =
                "File size too large, Please select another file.";
              this.image = "";
            } else {
              this.fileMessage = "";
              var fileReader = new FileReader();
              fileReader.onload = (e) => {
                this.image = e.target.result;
              };
              fileReader.readAsDataURL(check[0]);
            }
          }
        }
      },
      uploadImage() {
        this.setStatus(true);
        const formData = new FormData();
        formData.append("file", this.image);
        formData.append(
          "upload_preset",
          process.env.VUE_APP_CLOUDINARY_UPLOAD_SECOND_PRESET
        );
        this.loading = true;
        var instance = axios.create();
        // eslint-disable-next-line dot-notation
        delete instance.defaults.headers.common["Authorization"];
        instance
          .post(
            "https://api.Cloudinary.com/v1_1/djalafcj9/image/upload",
            formData
          )
          .then((response) => {
            this.loading = false;
            this.image = response.data.secure_url;
            this.updateLogo();
          })
          .catch((err) => {
            this.loading = false;
            responseHandler.handleError(err);
          });
      },
      async updateLogo() {
        try {
          const response = await this.updateOrgDetails({ image: this.image });
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
          this.getOrganisationProfile();
        } catch (error) {
          return error;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .company-info {
    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      padding: 24px;
      border-bottom: 1px solid #E2E8F0;
      gap: 16px;
      flex-wrap: wrap;
    }

    &__header-content {
      flex: 1;
      min-width: 200px;
    }

    &__title {
      font-family: 'DM Sans', sans-serif;
      font-size: 18px;
      font-weight: 500;
      color: #0F172A;
      margin: 0 0 4px 0;
    }

    &__subtitle {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }

    &__edit-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      background: white;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #0F172A;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        border-color: #4D84FF;
        color: #4D84FF;
      }
    }

    &__section {
      padding: 24px;
      border-bottom: 1px solid #E2E8F0;
    }

    &__logo-area {
      display: flex;
      align-items: flex-start;
      gap: 24px;
      flex-wrap: wrap;
    }

    &__logo-wrapper {
      position: relative;
      width: 120px;
      height: 120px;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid #E2E8F0;
      background: #F8FAFC;
    }

    &__logo-loading {
      position: absolute;
      inset: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }

    &__spinner {
      width: 24px;
      height: 24px;
      border: 2px solid #E2E8F0;
      border-top-color: #4D84FF;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    &__logo {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &__logo-actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__upload-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #0F172A;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        background: white;
        border-color: #4D84FF;
        color: #4D84FF;
      }
    }

    &__file-input {
      position: absolute;
      width: 0;
      height: 0;
      opacity: 0;
      pointer-events: none;
    }

    &__upload-hint {
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      font-weight: 400;
      color: #94A3B8;
    }

    &__error {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 400;
      color: #EF4444;
      margin-top: 12px;
    }

    &__grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0;

      @media (min-width: 640px) {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    &__item {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 20px 24px;
      border-bottom: 1px solid #F1F5F9;

      @media (min-width: 640px) {
        &:nth-child(odd) {
          border-right: 1px solid #F1F5F9;
        }
      }

      &:last-child {
        border-bottom: none;
      }
    }

    &__label {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 400;
      color: #64748B;
    }

    &__value {
      font-family: 'DM Sans', sans-serif;
      font-size: 15px;
      font-weight: 500;
      color: #0F172A;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
